import React, { useMemo } from 'react';
import { Table, ActionIcon } from '@mantine/core';
import { IconPlus, IconX } from '@tabler/icons-react';
import { FormDefinitionField, FormItem, FormTableDesign, FormTableRow, FormTableValue } from '@/interfaces/api/models';
import { decodeFormItemValue, encodeFormItemValue, FormFieldValue, getTableCellID, parseJsonSafely, SignaturePreview } from '../form-helpers';
import FormFieldRenderer from '../FormFieldRenderer';
import styles from './FormFieldTable.module.css';

export interface FormFieldTableProps {
    field: FormDefinitionField;
    value: string | null;
    onChange: (value: string) => void;
    // The owning FormItem's SectionGroup — part of every cell's synthetic ID, so cells in
    // different instances of a repeatable section stay distinguishable.
    sectionGroup?: number;
    disabled?: boolean;
    // Same resolvers `FormSection` hands to top-level fields, applied here per cell.
    resolveFieldOptions?: (field: FormDefinitionField) => string[];
    onUpload?: (file: File) => Promise<string>;
    resolveImageSrc?: (attachmentID: string) => string;
    buildSignatureContext?: (item: FormItem) => { ensurePersisted: () => Promise<void>; itemID: string };
    // The form's whole error map. `buildValidator` expands each required column into a per-cell
    // entry keyed by `getTableCellID`, so a cell finds its own error under the very ID it builds
    // for its synthetic field below.
    cellErrors?: Record<string, string>;
    // Clears one cell's error (by its synthetic `getTableCellID` key) — called on every edit, the
    // same "clear as soon as the user touches it" behaviour non-table fields get for free from
    // `@mantine/form`'s own `setFieldValue`, which only ever clears the exact path it was called
    // with (the table's own item ID, not any of its cells' synthetic IDs).
    onClearCellError?: (path: string) => void;
    resolveSignaturePreview?: (signatureID: string) => SignaturePreview | null;
    onSignatureSaved?: (signature: any) => void;
}

function parseValue(value: string | null): FormTableValue {
    if (!value) return { Rows: [] };
    try {
        return JSON.parse(value);
    } catch {
        return { Rows: [] };
    }
}

// Only used for compact (1-2 column) tables, whose columns otherwise shrink to fit their content
// — different field types need very different amounts of room to stay usable there, e.g. a
// Number input reads fine narrow but a LongText/DateTime column shouldn't get squeezed to the
// same width. A 3+ column (non-compact) table skips this entirely and sizes columns naturally.
const CELL_MIN_WIDTHS: Record<string, number> = {
    String: 150,
    LongText: 260,
    Number: 110,
    Checkbox: 90,
    Boolean: 140,
    // ScDateControl formats as "D MMMM, YYYY" (e.g. "15 September, 2026") - the full month name
    // needs more room than a numeric date format would.
    Date: 220,
    DateTime: 350,
    Select: 160,
    MultiSelect: 200,
    Image: 140,
    Signature: 180,
};
const DEFAULT_CELL_MIN_WIDTH = 160;

function getCellMinWidth(dataType: string): number {
    return CELL_MIN_WIDTHS[dataType] ?? DEFAULT_CELL_MIN_WIDTH;
}

function FormFieldTable({ field, value, onChange, sectionGroup = 0, disabled, resolveFieldOptions, onUpload, resolveImageSrc, buildSignatureContext, cellErrors, onClearCellError, resolveSignaturePreview, onSignatureSaved }: FormFieldTableProps) {
    const design: FormTableDesign = useMemo(() => parseJsonSafely<FormTableDesign>(field.DataOption, {} as FormTableDesign), [field.DataOption]);
    const parsed = parseValue(value);

    const rows: FormTableRow[] = design.LabelRows
        ? design.RowDefinitions.map((rowDef) => parsed.Rows.find((r) => r.RowName === rowDef.Name) ?? { RowName: rowDef.Name, Columns: [] })
        : parsed.Rows.length > 0 ? parsed.Rows : [{ RowName: null, Columns: [] }];

    const commit = (nextRows: FormTableRow[]) => onChange(JSON.stringify({ Rows: nextRows }));

    const setCell = (rowIndex: number, columnName: string, columnDataType: string, cellValue: FormFieldValue) => {
        const encodedValue = encodeFormItemValue(cellValue, columnDataType);
        const next = rows.map((row, idx) => {
            if (idx !== rowIndex) return row;
            const columns = row.Columns.filter((c) => c.ColumnName !== columnName);
            columns.push({ ColumnName: columnName, Value: encodedValue });
            return { ...row, Columns: columns };
        });
        commit(next);
    };

    const addRow = () => commit([...rows, { RowName: null, Columns: [] }]);
    const removeRow = (rowIndex: number) => commit(rows.filter((_, idx) => idx !== rowIndex));

    const canAddRow = !design.LabelRows && !disabled && (!design.LimitRows || rows.length < design.MaximumRows);
    const canRemoveRow = !design.LabelRows && !disabled && rows.length > 1;

    // A 1-2 column table stretched to the full width of the modal looks odd (a single input
    // spanning edge to edge) — let those shrink to fit their (min-width-floored) columns instead.
    // Once there's enough columns to fill the space, the default full width reads fine and each
    // column is left to size itself from its field's own content.
    const isCompact = design.ColumnDefinitions.length <= 2;

    return (
        <div className={`${styles.tableWrapper} ${isCompact ? styles.tableWrapperCompact : ''}`}>
            <Table className={isCompact ? styles.tableCompact : undefined}>
                <Table.Thead>
                    <Table.Tr>
                        {design.LabelRows && <Table.Th className={`${styles.headerCell} ${styles.rowLabelCell}`} />}
                        {design.ColumnDefinitions.map((col) => (
                            <Table.Th key={col.Name} className={styles.headerCell}>{col.Label}{col.Required ? ' *' : ''}</Table.Th>
                        ))}
                        {!design.LabelRows && <Table.Th className={styles.headerCell} />}
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {rows.map((row, rowIndex) => (
                        <Table.Tr key={row.RowName ?? rowIndex}>
                            {design.LabelRows && <Table.Td className={`${styles.rowLabelCell} ${styles.rowLabelBodyCell}`}>{design.RowDefinitions.find((r) => r.Name === row.RowName)?.Label}</Table.Td>}
                            {design.ColumnDefinitions.map((col) => {
                                const cell = row.Columns.find((c) => c.ColumnName === col.Name);
                                // `MasterID: col.Name` mirrors the legacy editor, which keyed a column's
                                // FormDataOptions override on the column name.
                                const cellField: FormDefinitionField = { ID: getTableCellID(col.Name, row.RowName ?? '', design.LabelRows ? '' : rowIndex, sectionGroup), MasterID: col.Name, Description: '', DataType: col.DataType, DataOption: col.DataOption ?? undefined, Required: col.Required };
                                const decodedValue = decodeFormItemValue(cell?.Value ?? null, col.DataType);
                                const cellItem: FormItem = { ID: cellField.ID!, DataType: col.DataType, Description: '', Required: col.Required };
                                return (
                                    // `data-form-item-id` mirrors the synthetic cell ID `buildValidator`
                                    // keys this cell's error under, so `scrollToFirstError` can land on the
                                    // exact cell rather than just the table.
                                    <Table.Td key={col.Name} className={styles.dataCell} style={{ minWidth: getCellMinWidth(col.DataType) }} data-form-item-id={cellField.ID}>
                                        <FormFieldRenderer
                                            field={cellField}
                                            value={decodedValue}
                                            onChange={(val) => {
                                                setCell(rowIndex, col.Name, col.DataType, val);
                                                onClearCellError?.(cellField.ID!);
                                            }}
                                            error={cellErrors?.[cellField.ID!]}
                                            disabled={disabled}
                                            availableOptions={resolveFieldOptions?.(cellField)}
                                            resolveFieldOptions={resolveFieldOptions}
                                            onUpload={onUpload}
                                            resolveImageSrc={resolveImageSrc}
                                            buildSignatureContext={buildSignatureContext}
                                            signatureContext={col.DataType === 'Signature' ? buildSignatureContext?.(cellItem) : undefined}
                                            resolveSignaturePreview={resolveSignaturePreview}
                                            onSignatureSaved={onSignatureSaved}
                                            inTable
                                        />
                                    </Table.Td>
                                );
                            })}
                            {canRemoveRow && (
                                <Table.Td>
                                    <ActionIcon variant="subtle" color="red" aria-label="remove row" onClick={() => removeRow(rowIndex)}>
                                        <IconX size={16} />
                                    </ActionIcon>
                                </Table.Td>
                            )}
                        </Table.Tr>
                    ))}
                    {canAddRow && (
                        <Table.Tr>
                            <Table.Td colSpan={design.ColumnDefinitions.length + 1}>
                                <ActionIcon variant="subtle" aria-label="add row" onClick={addRow}>
                                    <IconPlus size={16} />
                                </ActionIcon>
                            </Table.Td>
                        </Table.Tr>
                    )}
                </Table.Tbody>
            </Table>
        </div>
    );
}

export default React.memo(FormFieldTable);
